import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { StatusIcon } from "@/components/StatusIcon";
import { Check, AlertCircle, ArrowLeft, ArrowRight, Flower2, Calendar, CreditCard } from "lucide-react";

const SIZE_DESCRIPTIONS = {
  small: "Small bouquet, ~10 stems. Lovely for small households, casual flower lovers, or a thoughtful gift.",
  medium: "Medium bouquet, ~15 stems. Made for regular flower enthusiasts, family homes, or small businesses.",
  large: "Large bouquet, ~25 stems. For serious flower lovers, offices, and hospitality venues.",
};

const FREQUENCY_LABEL = { weekly: "every week", fortnightly: "every two weeks", monthly: "once a month" };

export default function SubscribePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [season, setSeason] = useState(null);
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState("");

  // Wizard state
  const [step, setStep] = useState(1); // 1 size, 2 frequency, 3 duration, 4 payment, 5 details, 6 review
  const [size, setSize] = useState("medium");
  const [frequency, setFrequency] = useState("fortnightly");
  const [durationMode, setDurationMode] = useState("full_season");
  const [paymentMode, setPaymentMode] = useState("full"); // full | deposit
  const [recipient, setRecipient] = useState({
    recipient_name: "",
    recipient_email: "",
    delivery_address: "",
    delivery_suburb: "",
    delivery_city: "",
    delivery_postcode: "",
    delivery_notes: "",
    is_gift: false,
    gift_message: "",
    preferred_start_date: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/public/seasons/current");
        if (!cancelled) {
          setSeason(data.season);
          setPlans(data.plans || []);
        }
      } catch (e) {
        if (!cancelled) setError(formatApiError(e.response?.data?.detail) || "Could not load subscription options.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // The selected plan (size + frequency combination)
  const selectedPlan = useMemo(() => {
    if (durationMode === "single") {
      // For single delivery, frequency doesn't matter for # deliveries, but we still need a plan row.
      // Pick the closest matching plan by size; default to weekly.
      return plans.find((p) => p.size === size && p.frequency === frequency) ||
             plans.find((p) => p.size === size);
    }
    return plans.find((p) => p.size === size && p.frequency === frequency);
  }, [plans, size, frequency, durationMode]);

  const pricing = useMemo(() => {
    if (!selectedPlan) return null;
    return (selectedPlan.duration_options || []).find((o) => o.duration_mode === durationMode);
  }, [selectedPlan, durationMode]);

  const planUnavailable = selectedPlan && ["closed", "waitlist_only"].includes(selectedPlan.status);
  const planFull = selectedPlan && selectedPlan.status === "full";
  const spotsLeft = selectedPlan?.max_subscribers
    ? Math.max(0, selectedPlan.max_subscribers - (selectedPlan.current_subscribers || 0))
    : null;
  const isSingle = durationMode === "single";

  const next = () => setStep((s) => Math.min(6, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  const updRecipient = (k, v) => setRecipient((r) => ({ ...r, [k]: v }));

  const detailsValid =
    recipient.recipient_name.trim().length >= 2 &&
    /\S+@\S+\.\S+/.test(recipient.recipient_email) &&
    recipient.delivery_address.trim().length >= 3 &&
    recipient.delivery_city.trim().length >= 2;

  const submitCheckout = async () => {
    if (!selectedPlan || !pricing) return;
    setBusy(true);
    setError("");
    try {
      const origin = window.location.origin;
      const body = {
        plan_id: selectedPlan.id,
        duration_mode: durationMode,
        payment_mode: isSingle ? "full" : paymentMode,
        origin_url: origin,
        ...recipient,
      };
      const { data } = await api.post("/checkout/subscription", body);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError("Stripe did not return a checkout URL. Please try again.");
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Checkout failed.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="club-theme min-h-screen">
        <PublicHeader />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center font-serif italic text-[var(--bloom-ink-soft)]">
          Loading the bloom box…
        </div>
        <PublicFooter />
      </div>
    );
  }

  if (!season || plans.length === 0) {
    return (
      <div className="club-theme min-h-screen">
        <PublicHeader />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-serif text-4xl mb-4">Subscriptions open soon</h1>
          <p className="font-serif italic text-[var(--bloom-ink-soft)]">
            We're prepping the next Bloom Box season. Join the waitlist and we'll let you know the moment it opens.
          </p>
          <button onClick={() => navigate("/waitlist")} className="bloom-btn bloom-btn-primary mt-8" data-testid="goto-waitlist">
            Join the waitlist
          </button>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="club-theme min-h-screen" data-testid="subscribe-page">
      <PublicHeader />

      <section className="max-w-5xl mx-auto px-6 md:px-12 pt-16 pb-8">
        <div className="bloom-divider mb-6">✦ {season.name} ✦</div>
        <h1 className="font-serif text-5xl md:text-6xl leading-[0.95]">
          Build your <em>bloom box</em>.
        </h1>
        <p className="font-serif italic text-xl text-[var(--bloom-ink-soft)] mt-4 max-w-2xl">
          Six steps, no fuss. Pick your size, how often, how long, and we'll do the rest.
          You can pause, gift, or cancel any time.
        </p>
      </section>

      {/* Stepper */}
      <section className="max-w-5xl mx-auto px-6 md:px-12">
        <ol className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-8" data-testid="subscribe-stepper">
          {[
            "Size", "Frequency", "Duration", "Payment", "Details", "Review",
          ].map((label, i) => {
            const n = i + 1;
            const done = step > n;
            const active = step === n;
            return (
              <li
                key={label}
                className={`px-3 py-2.5 text-[10px] tracking-[0.18em] uppercase text-center border ${
                  active
                    ? "bg-[var(--bloom-ink)] text-[#FAF4FA] border-[var(--bloom-ink)]"
                    : done
                    ? "bg-[var(--bloom-surface)] text-[var(--bloom-ink-soft)] border-[var(--bloom-border)]"
                    : "bg-transparent text-[var(--bloom-ink-muted)] border-[var(--bloom-border)]"
                }`}
                style={{ borderRadius: 999 }}
                data-testid={`step-${n}`}
              >
                {n}. {label}
              </li>
            );
          })}
        </ol>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-24">
        <div className="bloom-card p-6 md:p-10">
          {step === 1 && (
            <StepSize size={size} setSize={setSize} />
          )}

          {step === 2 && (
            <StepFrequency frequency={frequency} setFrequency={setFrequency} />
          )}

          {step === 3 && (
            <StepDuration
              durationMode={durationMode}
              setDurationMode={setDurationMode}
              plan={selectedPlan}
              isSingle={isSingle}
            />
          )}

          {step === 4 && (
            <StepPayment
              paymentMode={paymentMode}
              setPaymentMode={setPaymentMode}
              pricing={pricing}
              isSingle={isSingle}
            />
          )}

          {step === 5 && (
            <StepDetails
              recipient={recipient}
              updRecipient={updRecipient}
              season={season}
              durationMode={durationMode}
            />
          )}

          {step === 6 && (
            <StepReview
              plan={selectedPlan}
              pricing={pricing}
              paymentMode={isSingle ? "full" : paymentMode}
              recipient={recipient}
              season={season}
              durationMode={durationMode}
            />
          )}

          {/* Plan health flags */}
          {planUnavailable && step >= 4 && (
            <div className="mt-6 p-4 border border-[var(--bloom-warning)] text-[var(--bloom-warning)] font-serif italic" data-testid="plan-unavailable">
              This combination isn't open right now. Try a different size or frequency, or join the waitlist.
            </div>
          )}
          {planFull && step >= 4 && (
            <div className="mt-6 p-4 border border-[var(--bloom-warning)] text-[var(--bloom-warning)] font-serif italic" data-testid="plan-full">
              This plan is full. Try a different size, frequency, or join the waitlist.
            </div>
          )}
          {!planFull && !planUnavailable && spotsLeft !== null && spotsLeft <= 15 && step >= 2 && (
            <div className="mt-6 p-3 border border-[var(--bloom-brand)] bg-[var(--bloom-surface)] text-[var(--bloom-brand)] font-serif italic text-sm" data-testid="plan-spots-left">
              ✦ Only {spotsLeft} spot{spotsLeft !== 1 ? "s" : ""} left on this plan for the season — secure yours soon.
            </div>
          )}

          {error && (
            <div className="mt-6 flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic" data-testid="subscribe-error">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          <div className="mt-10 flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={back}
              disabled={step === 1 || busy}
              className="bloom-btn bloom-btn-outline disabled:opacity-30"
              data-testid="subscribe-back"
            >
              <ArrowLeft size={14} className="mr-2 inline" /> Back
            </button>

            {step < 6 ? (
              <button
                type="button"
                onClick={next}
                disabled={
                  busy ||
                  (step === 5 && !detailsValid) ||
                  (step >= 4 && (planUnavailable || planFull))
                }
                className="bloom-btn bloom-btn-primary disabled:opacity-40"
                data-testid="subscribe-next"
              >
                Continue <ArrowRight size={14} className="ml-2 inline" />
              </button>
            ) : (
              <button
                type="button"
                onClick={submitCheckout}
                disabled={busy || !pricing || planUnavailable || planFull}
                className="bloom-btn bloom-btn-primary disabled:opacity-40"
                data-testid="subscribe-pay"
              >
                {busy ? "Redirecting…" : (
                  <>
                    Pay with Stripe <CreditCard size={14} className="ml-2 inline" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

/* ---------- Step components ---------- */

function StepSize({ size, setSize }) {
  return (
    <div data-testid="step-size">
      <h2 className="font-serif text-3xl mb-2">Choose your size</h2>
      <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-8">Three bouquet sizes to suit your home.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { key: "small", title: "Small", price: 65, iconKey: "subscription.small" },
          { key: "medium", title: "Medium", price: 90, iconKey: "subscription.medium" },
          { key: "large", title: "Large", price: 120, iconKey: "subscription.large" },
        ].map((s) => {
          const active = size === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setSize(s.key)}
              className={`text-left p-6 border transition ${
                active
                  ? "bg-[var(--bloom-ink)] text-[#FAF4FA] border-[var(--bloom-ink)]"
                  : "bg-white/60 border-[var(--bloom-border)] hover:border-[var(--bloom-ink)]"
              }`}
              style={{ borderRadius: 6 }}
              data-testid={`size-${s.key}`}
            >
              <div className="flex items-center justify-between mb-3">
                <StatusIcon name={s.iconKey} size={64} fallback={<Flower2 size={22} strokeWidth={1.5} />} />
                {active && <Check size={16} />}
              </div>
              <div className="font-serif text-2xl mb-1">{s.title}</div>
              <div className="text-[10px] tracking-[0.22em] uppercase opacity-80 mb-4">${s.price} per delivery</div>
              <p className="font-serif italic text-sm leading-relaxed opacity-90">{SIZE_DESCRIPTIONS[s.key]}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepFrequency({ frequency, setFrequency }) {
  return (
    <div data-testid="step-frequency">
      <h2 className="font-serif text-3xl mb-2">How often?</h2>
      <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-8">
        Choose your cadence. You can also book a single delivery on the next step.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { key: "weekly", title: "Weekly", sub: "A bouquet every week" },
          { key: "fortnightly", title: "Fortnightly", sub: "Every two weeks" },
          { key: "monthly", title: "Monthly", sub: "Once a month" },
        ].map((f) => {
          const active = frequency === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFrequency(f.key)}
              className={`text-left p-6 border transition ${
                active
                  ? "bg-[var(--bloom-ink)] text-[#FAF4FA] border-[var(--bloom-ink)]"
                  : "bg-white/60 border-[var(--bloom-border)] hover:border-[var(--bloom-ink)]"
              }`}
              style={{ borderRadius: 6 }}
              data-testid={`frequency-${f.key}`}
            >
              <div className="flex items-center justify-between mb-3">
                <Calendar size={22} strokeWidth={1.5} />
                {active && <Check size={16} />}
              </div>
              <div className="font-serif text-2xl mb-1">{f.title}</div>
              <div className="font-serif italic text-sm opacity-80">{f.sub}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepDuration({ durationMode, setDurationMode, plan, isSingle }) {
  const opts = plan?.duration_options || [];
  return (
    <div data-testid="step-duration">
      <h2 className="font-serif text-3xl mb-2">For how long?</h2>
      <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-8">
        Pick a single delivery, a single month, or the whole bloom season.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {opts.map((o) => {
          const active = durationMode === o.duration_mode;
          return (
            <button
              key={o.duration_mode}
              type="button"
              onClick={() => setDurationMode(o.duration_mode)}
              className={`flex items-center justify-between p-5 border transition text-left ${
                active
                  ? "bg-[var(--bloom-ink)] text-[#FAF4FA] border-[var(--bloom-ink)]"
                  : "bg-white/60 border-[var(--bloom-border)] hover:border-[var(--bloom-ink)]"
              }`}
              style={{ borderRadius: 6 }}
              data-testid={`duration-${o.duration_mode}`}
            >
              <div>
                <div className="font-serif text-xl">{o.label}</div>
                <div className="font-serif italic text-sm opacity-80 mt-0.5">
                  {o.delivery_count} {o.delivery_count === 1 ? "delivery" : "deliveries"}
                </div>
              </div>
              <div className="font-serif text-2xl">${o.total_value.toFixed(0)}</div>
            </button>
          );
        })}
      </div>
      {isSingle && (
        <p className="font-serif italic text-sm text-[var(--bloom-ink-muted)] mt-6">
          A single delivery is a one-off, no ongoing subscription, no commitment.
        </p>
      )}
    </div>
  );
}

function StepPayment({ paymentMode, setPaymentMode, pricing, isSingle }) {
  if (!pricing) return null;
  if (isSingle) {
    return (
      <div data-testid="step-payment">
        <h2 className="font-serif text-3xl mb-2">Payment</h2>
        <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-8">
          For a single delivery you'll just pay the bouquet price now.
        </p>
        <div className="p-6 border border-[var(--bloom-ink)] bg-[var(--bloom-surface)]" style={{ borderRadius: 6 }}>
          <div className="font-serif text-2xl">${pricing.total_value.toFixed(2)}</div>
          <div className="font-serif italic text-sm text-[var(--bloom-ink-soft)]">One-off payment via Stripe.</div>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="step-payment">
      <h2 className="font-serif text-3xl mb-2">How would you like to pay?</h2>
      <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-8">
        Pay in full and forget about it, or hold your spot with a 25% deposit and we'll bill the balance closer to season.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PaymentCard
          active={paymentMode === "full"}
          onClick={() => setPaymentMode("full")}
          title="Pay in full"
          amount={`$${pricing.total_value.toFixed(2)}`}
          sub="One-off charge now. Done and dusted."
          iconKey="subscription.paid_full"
          tid="pay-full"
        />
        <PaymentCard
          active={paymentMode === "deposit"}
          onClick={() => setPaymentMode("deposit")}
          title="25% deposit"
          amount={`$${pricing.deposit_amount.toFixed(2)} now`}
          sub={`Balance of $${(pricing.total_value - pricing.deposit_amount).toFixed(2)} closer to your first delivery.`}
          iconKey="subscription.deposit"
          tid="pay-deposit"
        />
      </div>
    </div>
  );
}

function PaymentCard({ active, onClick, title, amount, sub, iconKey, tid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left p-6 border transition ${
        active
          ? "bg-[var(--bloom-ink)] text-[#FAF4FA] border-[var(--bloom-ink)]"
          : "bg-white/60 border-[var(--bloom-border)] hover:border-[var(--bloom-ink)]"
      }`}
      style={{ borderRadius: 6 }}
      data-testid={tid}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="font-serif text-2xl">{title}</div>
        {iconKey && <StatusIcon name={iconKey} size={56} fallback={active ? <Check size={18} /> : null} />}
      </div>
      <div className="font-serif text-xl mb-1">{amount}</div>
      <div className="font-serif italic text-sm opacity-85">{sub}</div>
    </button>
  );
}

function StepDetails({ recipient, updRecipient, season, durationMode }) {
  return (
    <div data-testid="step-details">
      <h2 className="font-serif text-3xl mb-2">Where should we send the blooms?</h2>
      <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-8">
        We'll auto-create your subscriber account when payment clears, so use the email you check most.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Recipient name *">
          <input className="bloom-input" value={recipient.recipient_name} onChange={(e) => updRecipient("recipient_name", e.target.value)} data-testid="details-name" />
        </Field>
        <Field label="Email *">
          <input type="email" className="bloom-input" value={recipient.recipient_email} onChange={(e) => updRecipient("recipient_email", e.target.value)} data-testid="details-email" />
        </Field>
        <Field label="Delivery address *">
          <input className="bloom-input" value={recipient.delivery_address} onChange={(e) => updRecipient("delivery_address", e.target.value)} placeholder="123 Camellia Street" data-testid="details-address" />
        </Field>
        <Field label="Suburb">
          <input className="bloom-input" value={recipient.delivery_suburb} onChange={(e) => updRecipient("delivery_suburb", e.target.value)} data-testid="details-suburb" />
        </Field>
        <Field label="City / town *">
          <input className="bloom-input" value={recipient.delivery_city} onChange={(e) => updRecipient("delivery_city", e.target.value)} data-testid="details-city" />
        </Field>
        <Field label="Postcode">
          <input className="bloom-input" value={recipient.delivery_postcode} onChange={(e) => updRecipient("delivery_postcode", e.target.value)} data-testid="details-postcode" />
        </Field>
        {durationMode !== "single" && (
          <Field label="Preferred start (optional)">
            <input
              type="date"
              min={season.starts_on}
              max={season.ends_on}
              className="bloom-input"
              value={recipient.preferred_start_date}
              onChange={(e) => updRecipient("preferred_start_date", e.target.value)}
              data-testid="details-start"
            />
          </Field>
        )}
        <div className="md:col-span-2">
          <Field label="Delivery notes">
            <textarea rows={2} className="bloom-input" style={{ resize: "vertical" }} value={recipient.delivery_notes} onChange={(e) => updRecipient("delivery_notes", e.target.value)} data-testid="details-notes" />
          </Field>
        </div>

        <div className="md:col-span-2 flex items-center gap-3 pt-2">
          <input
            id="is_gift"
            type="checkbox"
            checked={recipient.is_gift}
            onChange={(e) => updRecipient("is_gift", e.target.checked)}
            className="w-4 h-4"
            data-testid="details-gift"
          />
          <label htmlFor="is_gift" className="font-serif text-base">This is a gift.</label>
        </div>
        {recipient.is_gift && (
          <div className="md:col-span-2">
            <Field label="Gift message">
              <textarea rows={3} className="bloom-input" style={{ resize: "vertical" }} value={recipient.gift_message} onChange={(e) => updRecipient("gift_message", e.target.value)} data-testid="details-gift-message" />
            </Field>
          </div>
        )}
      </div>
    </div>
  );
}

function StepReview({ plan, pricing, paymentMode, recipient, season, durationMode }) {
  if (!plan || !pricing) return null;
  const chargeNow = paymentMode === "full" ? pricing.total_value : pricing.deposit_amount;
  const balance = pricing.total_value - chargeNow;
  return (
    <div data-testid="step-review">
      <h2 className="font-serif text-3xl mb-2">Review and pay</h2>
      <p className="font-serif italic text-[var(--bloom-ink-soft)] mb-8">A final glance before we hand you to Stripe.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-6 border border-[var(--bloom-border)] bg-white/50" style={{ borderRadius: 6 }}>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mb-3">Your bloom box</div>
          <div className="font-serif text-2xl capitalize">{plan.size} {FREQUENCY_LABEL[plan.frequency]}</div>
          <div className="font-serif italic text-[var(--bloom-ink-soft)] mt-1">
            {pricing.label} · {pricing.delivery_count} {pricing.delivery_count === 1 ? "delivery" : "deliveries"}
          </div>
          <div className="mt-4 font-serif text-sm text-[var(--bloom-ink-soft)]">
            <div>Season: {season.name}</div>
            <div>Per delivery: ${plan.per_delivery.toFixed(2)}</div>
            {recipient.preferred_start_date && durationMode !== "single" && (
              <div>Preferred start: {recipient.preferred_start_date}</div>
            )}
          </div>
        </div>
        <div className="p-6 border border-[var(--bloom-border)] bg-white/50" style={{ borderRadius: 6 }}>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--bloom-ink-muted)] mb-3">Delivery</div>
          <div className="font-serif text-base">{recipient.recipient_name}</div>
          <div className="font-serif italic text-[var(--bloom-ink-soft)] text-sm">{recipient.recipient_email}</div>
          <div className="font-serif text-sm mt-3 text-[var(--bloom-ink-soft)]">
            {recipient.delivery_address}{recipient.delivery_suburb ? `, ${recipient.delivery_suburb}` : ""}
            <br />
            {recipient.delivery_city}{recipient.delivery_postcode ? ` ${recipient.delivery_postcode}` : ""}
          </div>
          {recipient.is_gift && (
            <div className="mt-3 font-serif italic text-sm text-[var(--bloom-accent)]">Gift order</div>
          )}
        </div>
        <div className="md:col-span-2 p-6 border border-[var(--bloom-ink)] bg-[var(--bloom-surface)]" style={{ borderRadius: 6 }} data-testid="review-totals">
          <div className="flex items-center justify-between font-serif text-base">
            <span>Total subscription value</span>
            <span>${pricing.total_value.toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between font-serif text-xl mt-1">
            <span>Paying now</span>
            <span data-testid="review-charge-now">${chargeNow.toFixed(2)}</span>
          </div>
          {balance > 0 && (
            <div className="flex items-center justify-between font-serif italic text-sm text-[var(--bloom-ink-soft)] mt-1">
              <span>Balance due closer to your first delivery</span>
              <span>${balance.toFixed(2)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="bloom-label mb-2">{label}</div>
      {children}
    </div>
  );
}
